/**
 * Time utility helpers.
 * Provides consistent human-readable duration formatting across quiz surfaces.
 */

/**
 * Convert a duration in seconds to a compact human-readable label.
 * Examples: 45 -> "45s", 90 -> "1m 30s", 3661 -> "1h 1m 1s".
 *
 * @param {number|string|null|undefined} value - Duration value expressed in seconds.
 * @returns {string} Formatted duration label or "-" when the value is missing/invalid.
 */
export const formatDurationFromSeconds = (value) => {
  if (value === null || value === undefined) return '-';

  const totalSeconds = Math.max(0, Math.round(Number(value)));

  if (Number.isNaN(totalSeconds)) {
    return '-';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`);
  }

  parts.push(`${seconds}s`);

  return parts.join(' ');
};

/**
 * Convert a duration in milliseconds to a compact human-readable label.
 * Internally delegates to the seconds formatter to keep output identical everywhere.
 *
 * @param {number|string|null|undefined} value - Duration value expressed in milliseconds.
 * @returns {string} Formatted duration label or "-" when the value is missing/invalid.
 */
export const formatDurationFromMilliseconds = (value) => {
  if (value === null || value === undefined) return '-';
  return formatDurationFromSeconds(Number(value) / 1000);
};